import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Button, Divider, Dropdown, Space, Tooltip } from 'antd';
import { BoldOutlined, ItalicOutlined, LinkOutlined, OrderedListOutlined, TagOutlined, UnderlineOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useMergeTags } from '../features/email/api';
import { mentionQuery, MentionMenu, useMentionMenu, type MentionOption } from './Mentions';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** show the merge-tag inserter */
  mergeTags?: boolean;
  /** typing `@` opens a menu of members and organizations to mention */
  mentions?: boolean;
}

/** Slack, Gmail, X and many sites copy emoji as `<img alt="🚀">`; the editor has no image node, so keep the alt text. */
const EMOJI_IMG = /<img\b[^>]*?\balt=(["'])(\p{Extended_Pictographic}[^"']{0,15}|\p{Regional_Indicator}{2})\1[^>]*>/gu;
const emojiImagesToText = (html: string) => html.replace(EMOJI_IMG, (_, _q, alt: string) => alt);

/** Small Tiptap editor for emails and signatures. Emits HTML. */
export function RichTextEditor({ value, onChange, placeholder, minHeight = 180, mergeTags = true, mentions = false }: Props) {
  const tags = useMergeTags();
  // the `@query` being typed, where it starts in the document, and where to show the menu
  const [mention, setMention] = useState<{ query: string; from: number; left: number; top: number } | null>(null);
  const handleMentionKey = useRef<(key: string) => boolean>(() => false);
  const editor = useEditor({
    extensions: [StarterKit, Underline, Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }), Placeholder.configure({ placeholder: placeholder ?? 'Write…' })],
    content: value,
    editorProps: { transformPastedHTML: emojiImagesToText, handleKeyDown: (_view, e) => handleMentionKey.current(e.key) },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
    onSelectionUpdate: ({ editor: e }) => {
      if (!mentions) return;
      const { $from, empty } = e.state.selection;
      const query = empty && !e.isActive('code') ? mentionQuery($from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc')) : null;
      if (query === null) return setMention(null);
      const from = $from.pos - query.length - 1;
      const at = e.view.coordsAtPos(from);
      setMention({ query, from, left: at.left, top: at.bottom + 4 });
    },
    onBlur: () => setMention(null),
  });

  const pickMention = (o: MentionOption) => {
    if (!editor || !mention) return;
    editor
      .chain()
      .focus()
      .insertContentAt({ from: mention.from, to: editor.state.selection.from }, [
        { type: 'text', text: `@${o.name}`, marks: [{ type: 'link', attrs: { href: o.href, class: 'mention' } }] },
        { type: 'text', text: ' ' },
      ])
      .run();
    setMention(null);
  };
  const menu = useMentionMenu(mention?.query ?? null, pickMention, () => setMention(null));
  handleMentionKey.current = menu.handleKey;

  // Sync external resets (e.g. clearing after send) without clobbering typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (value !== current && (value === '' || !editor.isFocused)) editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;
  const btn = (active: boolean, icon: React.ReactNode, title: string, run: () => void) => (
    <Tooltip title={title}>
      <Button size="small" type={active ? 'primary' : 'text'} icon={icon} aria-label={title} aria-pressed={active} onMouseDown={(e) => { e.preventDefault(); run(); }} />
    </Tooltip>
  );
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return;
    if (!url) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, background: '#fff' }}>
      <Space size={0} style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
        {btn(editor.isActive('bold'), <BoldOutlined />, 'Bold', () => editor.chain().focus().toggleBold().run())}
        {btn(editor.isActive('italic'), <ItalicOutlined />, 'Italic', () => editor.chain().focus().toggleItalic().run())}
        {btn(editor.isActive('underline'), <UnderlineOutlined />, 'Underline', () => editor.chain().focus().toggleUnderline().run())}
        <Divider orientation="vertical" style={{ margin: '0 4px' }} />
        {btn(editor.isActive('bulletList'), <UnorderedListOutlined />, 'Bullet list', () => editor.chain().focus().toggleBulletList().run())}
        {btn(editor.isActive('orderedList'), <OrderedListOutlined />, 'Numbered list', () => editor.chain().focus().toggleOrderedList().run())}
        {btn(editor.isActive('link'), <LinkOutlined />, 'Link', setLink)}
        {mergeTags && (
          <>
            <Divider orientation="vertical" style={{ margin: '0 4px' }} />
            <Dropdown
              trigger={['click']}
              menu={{
                items: (tags.data ?? []).map((t) => ({ key: t.tag, label: `${t.label}  {{${t.tag}}}` })),
                onClick: ({ key }) => editor.chain().focus().insertContent(`{{${key}}}`).run(),
                // long lists (many custom fields) scroll instead of growing past the screen edge
                style: { maxHeight: 'min(360px, 50vh)', overflowY: 'auto' },
              }}
              placement="bottomLeft"
            >
              <Button size="small" type="text" icon={<TagOutlined />} disabled={!tags.data?.length} loading={tags.isLoading}>
                Merge tag
              </Button>
            </Dropdown>
          </>
        )}
      </Space>
      <EditorContent editor={editor} className="rte" style={{ minHeight, padding: '8px 12px' }} />
      {mention && menu.open && <MentionMenu at={mention} options={menu.options} loading={menu.loading} index={menu.index} onHover={menu.setIndex} onPick={pickMention} />}
    </div>
  );
}
